import React, { useState, useEffect, useRef } from "react";
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline";

/**
 * Reusable Async Autocomplete component for UI 2.0
 * Fetches options asynchronously based on user input with debouncing
 *
 * @param {Object} props
 * @param {function} props.fetchOptions - Async function that receives search query and returns array of { value, label } objects
 * @param {string} props.value - Currently selected value
 * @param {function} props.onChange - Callback when value changes (receives value string and full option object)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Optional label text
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.className - Additional classes for the container
 * @param {number} props.debounceMs - Debounce delay in milliseconds (default: 500)
 * @param {number} props.minChars - Minimum characters before triggering search (default: 2)
 * @param {boolean} props.fetchOnEmpty - Whether to fetch options when query is empty (default: false)
 */
export default function AsyncAutocomplete({
  fetchOptions,
  value,
  onChange,
  placeholder = "Type to search...",
  label,
  disabled = false,
  className = "",
  debounceMs = 500,
  minChars = 2,
  fetchOnEmpty = false,
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Fetch options from the API based on the search query
   * @param {string} searchQuery - The search term entered by the user
   */
  const loadOptions = async (searchQuery) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const results = await fetchOptions(searchQuery, abortControllerRef.current.signal);
      setOptions(Array.isArray(results) ? results : []);
    } catch (err) {
      // Don't show error if request was aborted (normal behavior when user types)
      if (err.name !== "AbortError" && err.name !== "CanceledError") {
        console.error("Error fetching options:", err);
        setError("Failed to load options");
        setOptions([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Debounce the search query to avoid excessive API calls
   */
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Handle empty query case
    if (query.length === 0) {
      // If fetchOnEmpty is enabled, load options with empty query
      if (fetchOnEmpty) {
        debounceTimerRef.current = setTimeout(() => {
          loadOptions("");
        }, debounceMs);
      } else {
        setOptions([]);
      }
      return;
    }

    // Don't search if query is too short
    if (query.length < minChars) {
      return;
    }

    // Set new timer to trigger search after debounce delay
    debounceTimerRef.current = setTimeout(() => {
      loadOptions(query);
    }, debounceMs);

    // Cleanup function to clear timer on unmount or query change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, debounceMs, minChars, fetchOnEmpty]);

  /**
   * Load initial options on mount if fetchOnEmpty is enabled
   */
  useEffect(() => {
    if (fetchOnEmpty && !disabled) {
      loadOptions("");
    }
  }, [fetchOnEmpty, disabled]);

  /**
   * Cleanup abort controller on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Find the selected option when value changes
   */
  useEffect(() => {
    if (value) {
      const found = options.find((opt) => opt.value === value);
      if (found) {
        setSelectedOption(found);
      }
    } else {
      setSelectedOption(null);
    }
  }, [value, options]);

  /**
   * Handle option selection
   * @param {Object} option - The selected option object
   */
  const handleChange = (option) => {
    if (option) {
      setSelectedOption(option);
      onChange(option.value, option);
      setQuery("");
    }
  };

  /**
   * Handle input change - updates query and triggers search
   * @param {Event} event - Input change event
   */
  const handleInputChange = (event) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    
    // Clear selection if user starts typing
    if (selectedOption && newQuery !== selectedOption.label) {
      setSelectedOption(null);
      onChange("");
    }
  };

  /**
   * Handle input focus - select all text for easy replacement
   * @param {Event} event - Focus event
   */
  const handleFocus = (event) => {
    event.target.select();
  };

  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
          {label}
        </label>
      )}
      <Combobox value={selectedOption} onChange={handleChange} disabled={disabled}>
        <div className="tw-relative">
          <ComboboxInput
            className={`tw-w-full tw-rounded-lg tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-3 tw-py-2 tw-pr-8 tw-text-sm dark:tw-text-white tw-placeholder-gray-400 focus:tw-border-blue-500 focus:tw-outline-none ${
              disabled ? "tw-bg-gray-100 tw-text-gray-400 dark:tw-bg-gray-600 tw-cursor-not-allowed" : ""
            }`}
            displayValue={(option) => option?.label || ""}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
          />
          <ComboboxButton className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-2">
            {isLoading ? (
              <svg
                className="tw-animate-spin tw-h-4 tw-w-4 tw-text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="tw-opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="tw-opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <ChevronUpDownIcon
                className="tw-h-4 tw-w-4 tw-text-gray-400"
                aria-hidden="true"
              />
            )}
          </ComboboxButton>

          <ComboboxOptions className="tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-lg tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-text-sm tw-shadow-lg tw-ring-1 tw-ring-black/5 dark:tw-ring-white/10 focus:tw-outline-none">
            {error ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-3 tw-py-2 tw-text-rose-500 dark:tw-text-rose-400">
                {error}
              </div>
            ) : query.length > 0 && query.length < minChars ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-3 tw-py-2 tw-text-gray-500 dark:tw-text-gray-400">
                Type at least {minChars} characters to search
              </div>
            ) : isLoading ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-3 tw-py-2 tw-text-gray-500 dark:tw-text-gray-400 tw-flex tw-items-center tw-gap-2">
                <svg
                  className="tw-animate-spin tw-h-4 tw-w-4 tw-text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="tw-opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="tw-opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Searching...
              </div>
            ) : options.length === 0 && query.length >= minChars ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-3 tw-py-2 tw-text-gray-500 dark:tw-text-gray-400">
                No results found
              </div>
            ) : options.length === 0 && fetchOnEmpty ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-3 tw-py-2 tw-text-gray-500 dark:tw-text-gray-400">
                No options available
              </div>
            ) : (
              options.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option}
                  className="tw-relative tw-cursor-pointer tw-select-none tw-py-2 tw-pl-3 tw-pr-8 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-50 dark:data-[focus]:tw-bg-gray-700"
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`tw-block ${
                          selected ? "tw-font-medium" : "tw-font-normal"
                        }`}
                        title={option.label}
                      >
                        {option.label}
                      </span>
                      {selected && (
                        <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-2 tw-text-blue-600 dark:tw-text-blue-400">
                          <CheckIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}

